/**
 * The art pipeline's rules (issue #195), kept out of the Lit components so they
 * can be tested. The pipeline's rows are specs: a piece of art staff want, with
 * its type and that type's properties, priority, brief, reference images and
 * milestone. A commission is a job on a spec, and a spec can have several. The
 * grid mechanics (sorting, grouping, filtering, editing) are the shared
 * lib/smart-grid.js.
 */

/**
 * @typedef {Object} SpecCommission
 * @property {string} id
 * @property {string} slug
 * @property {string} status - open | assigned | completed | archived
 * @property {number|null} rate_cents
 * @property {string|null} assigned_to
 * @property {string|null} assigned_to_username
 * @property {string|null} offered_to
 * @property {string|null} offered_to_username
 */

/**
 * @typedef {Object} Spec
 * @property {string} id
 * @property {string} name
 * @property {string} asset_type
 * @property {Record<string, string>} properties - the type's own, e.g. a miniature's size
 * @property {string} priority
 * @property {string} status - draft | commissioned | assigned | done | archived: archived when staff archived it, otherwise from its commissions
 * @property {string|null} milestone_id
 * @property {string|null} milestone_name
 * @property {string|null} brief
 * @property {Array<{id: string, file_url: string, file_name: string, mime_type: string|null}>} reference_images
 * @property {SpecCommission[]} commissions
 * @property {number} position - its place in the manual order
 * @property {string} updated_at
 * @property {string|null} [archived_at] - when staff archived it; null when they haven't
 */

/**
 * @typedef {Object} Milestone
 * @property {string} id
 * @property {string} name
 * @property {string|null} target_date
 * @property {string|null} description
 */

/**
 * A spec's statuses, in pipeline order. They all come from its commissions:
 * draft until it has one, then its furthest-along live commission's progress,
 * or archived when every one is archived. Nothing sets one by hand.
 */
export const PIPELINE_STATUSES = [
    { value: 'draft', label: 'Draft', badge: 'bg-light text-dark border' },
    { value: 'commissioned', label: 'Commissioned', badge: 'bg-info text-dark' },
    { value: 'assigned', label: 'Assigned', badge: 'bg-warning text-dark' },
    { value: 'done', label: 'Done', badge: 'bg-success' },
    { value: 'archived', label: 'Archived', badge: 'bg-secondary' },
];

export const PRIORITIES = [
    { value: 'urgent', label: 'Urgent', badge: 'bg-danger' },
    { value: 'high', label: 'High', badge: 'bg-warning text-dark' },
    { value: 'medium', label: 'Medium', badge: 'bg-info text-dark' },
    { value: 'low', label: 'Low', badge: 'bg-secondary' },
];

export const SPEC_ASSET_TYPES = [
    { value: 'miniature', label: 'Miniature' },
    { value: 'image', label: 'Image' },
    { value: 'tactical-map', label: 'Tactical Map' },
    { value: 'mapmaking-tile-brush', label: 'Tile Brush' },
    { value: 'title-screen', label: 'Title Screen' },
    { value: 'map', label: 'Map' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'hook', label: 'Hook' },
];

/** Asset types a commission can be created for (they have phase workflows). */
export const COMMISSIONABLE_TYPES = [
    'miniature',
    'image',
    'title-screen',
    'tactical-map',
    'mapmaking-tile-brush',
];

/**
 * @typedef {Object} SpecProperty
 * @property {string} key
 * @property {string} label
 * @property {Array<{value: string, label: string}>} options
 * @property {boolean} [neededToCommission]
 */

/**
 * Each type's own properties, as the API checks them (model.ValidateSpecProperties).
 * @type {Record<string, SpecProperty[]>}
 */
export const SPEC_PROPERTIES = {
    miniature: [
        {
            key: 'size',
            label: 'Size',
            neededToCommission: true,
            options: [
                { value: 'T', label: 'Tiny' },
                { value: 'S', label: 'Small' },
                { value: 'M', label: 'Medium' },
                { value: 'L', label: 'Large' },
                { value: 'H', label: 'Huge' },
                { value: 'G', label: 'Gargantuan' },
            ],
        },
        {
            key: 'miniature_type',
            label: 'Miniature type',
            options: [
                { value: 'pc', label: 'PC' },
                { value: 'monster', label: 'Monster' },
            ],
        },
    ],
    'tactical-map': [
        {
            key: 'size',
            label: 'Size',
            options: [
                { value: 'S', label: 'Small (12x12)' },
                { value: 'M', label: 'Medium (24x24)' },
                { value: 'L', label: 'Large (48x48)' },
            ],
        },
    ],
};

/**
 * @param {string} assetType
 * @returns {SpecProperty[]}
 */
export function specPropertiesFor(assetType) {
    return SPEC_PROPERTIES[assetType] || [];
}

/**
 * A short description of a spec's properties, e.g. "Medium, PC".
 * @param {Spec} spec
 * @returns {string}
 */
export function describeProperties(spec) {
    return specPropertiesFor(spec.asset_type)
        .map((p) => p.options.find((o) => o.value === spec.properties?.[p.key])?.label)
        .filter(Boolean)
        .join(', ');
}

/**
 * Why a spec can't be commissioned yet, or null when it can.
 * @param {Spec} spec
 * @returns {string|null}
 */
export function cannotCommission(spec) {
    if (spec.archived_at) return 'Unarchive it before commissioning it.';
    if (!COMMISSIONABLE_TYPES.includes(spec.asset_type)) {
        return 'Commissions are for miniatures, images, title screens, tactical maps and tile brushes.';
    }
    if (!spec.brief?.trim()) return 'Add a brief before commissioning it.';
    const missing = specPropertiesFor(spec.asset_type).find(
        (p) => p.neededToCommission && !spec.properties?.[p.key],
    );
    if (missing) return `Set its ${missing.label.toLowerCase()} before commissioning it.`;
    return null;
}

/**
 * The rate, in cents, a new commission defaults to, as the API sets it
 * (commissions.DefaultRateCents); null when there's no default.
 * @param {string} assetType
 * @param {string|undefined} size
 * @returns {number|null}
 */
export function defaultRateCents(assetType, size) {
    const rates = {
        miniature: { T: 4000, S: 4000, M: 5000, L: 8000, H: 12000, G: 16000 },
        'tactical-map': { S: 15000, M: 35000, L: 60000 },
    };
    if (assetType === 'title-screen') return 40000;
    return rates[assetType]?.[size] ?? null;
}

/**
 * @param {string} status
 * @returns {{value: string, label: string, badge: string}}
 */
export function statusMeta(status) {
    return (
        PIPELINE_STATUSES.find((s) => s.value === status) || {
            value: status,
            label: status,
            badge: 'bg-secondary',
        }
    );
}

/**
 * @param {string} priority
 * @returns {{value: string, label: string, badge: string}}
 */
export function priorityMeta(priority) {
    return (
        PRIORITIES.find((p) => p.value === priority) || {
            value: priority,
            label: priority,
            badge: 'bg-secondary',
        }
    );
}

/**
 * @param {string} assetType
 * @returns {string}
 */
export function assetTypeLabel(assetType) {
    return SPEC_ASSET_TYPES.find((t) => t.value === assetType)?.label || assetType;
}

/**
 * Who a spec's live commissions are with: each assigned freelancer, or the one
 * a commission is offered to. Archived and completed commissions are left out.
 * @param {Spec} spec
 * @param {Map<string, string>} [userLabels] - user id → display name
 * @returns {Array<{id: string, label: string, offered: boolean}>}
 */
export function freelancersOf(spec, userLabels = new Map()) {
    const labelFor = (id, username) => userLabels.get(id) || username || 'Unknown user';
    const out = [];
    for (const c of spec.commissions || []) {
        if (c.status === 'archived' || c.status === 'completed') continue;
        if (c.assigned_to) {
            out.push({
                id: c.assigned_to,
                label: labelFor(c.assigned_to, c.assigned_to_username),
                offered: false,
            });
        } else if (c.offered_to) {
            out.push({
                id: c.offered_to,
                label: labelFor(c.offered_to, c.offered_to_username),
                offered: true,
            });
        }
    }
    return out;
}

/**
 * How far along a set of specs is: counts by status, and the share done.
 * @param {Spec[]} items
 * @returns {{total: number, counts: Record<string, number>, done: number, percentDone: number}}
 */
export function milestoneProgress(items) {
    /** @type {Record<string, number>} */
    const counts = Object.fromEntries(PIPELINE_STATUSES.map((s) => [s.value, 0]));
    for (const item of items) counts[item.status] = (counts[item.status] || 0) + 1;
    const total = items.length;
    const done = counts.done || 0;
    return { total, counts, done, percentDone: total ? Math.round((done / total) * 100) : 0 };
}

/**
 * A slug from a name, matching the API's model.Slugify.
 * @param {string} name
 * @returns {string}
 */
export function slugify(name) {
    return name
        .toLowerCase()
        .replace(/[ _]/g, '-')
        .replace(/[^a-z0-9-]+/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * A slug for a new commission on a spec: the spec's name, with a number on
 * the end when the spec's commissions already use it.
 * @param {Spec} spec
 * @returns {string}
 */
export function commissionSlugFor(spec) {
    const base = slugify(spec.name) || 'commission';
    const taken = new Set((spec.commissions || []).map((c) => c.slug));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
}

/**
 * The grid's starting view, before staff have one of their own: every spec
 * in staff's manual order (the order the API lists specs in, which dragging
 * changes), ungrouped.
 * @type {import('./smart-grid.js').GridState}
 */
export const DEFAULT_PIPELINE_STATE = {
    sort: null,
    group: '',
    filters: {},
    query: '',
};

/**
 * The fields the side panel edits, as a draft of a spec: '' for none.
 * @param {Spec} spec
 */
export function draftOf(spec) {
    return {
        name: spec.name,
        asset_type: spec.asset_type,
        properties: { ...(spec.properties || {}) },
        priority: spec.priority,
        milestone_id: spec.milestone_id || '',
        brief: spec.brief || '',
    };
}

/**
 * Whether two property sets hold the same values, ignoring blanks.
 * @param {Record<string, string>} a
 * @param {Record<string, string>} b
 */
function sameProperties(a, b) {
    const clean = (p) =>
        Object.entries(p || {})
            .filter(([, v]) => v)
            .sort(([x], [y]) => x.localeCompare(y));
    return JSON.stringify(clean(a)) === JSON.stringify(clean(b));
}

/**
 * The PATCH a draft makes: only the fields it changed from base, with '' as
 * null for the clearable ones and blank properties left out. A spec with a
 * commission keeps its type, so that's never sent for one.
 * @param {ReturnType<typeof draftOf>} base
 * @param {ReturnType<typeof draftOf>} draft
 * @param {boolean} commissioned
 * @returns {Record<string, any>}
 */
export function draftChanges(base, draft, commissioned) {
    /** @type {Record<string, any>} */
    const changes = {};
    for (const key of /** @type {(keyof typeof draft)[]} */ (Object.keys(draft))) {
        if (key === 'asset_type' && commissioned) continue;
        if (key === 'properties') {
            const typeChanged = !commissioned && base.asset_type !== draft.asset_type;
            if (typeChanged || !sameProperties(base.properties, draft.properties)) {
                changes.properties = Object.fromEntries(
                    Object.entries(draft.properties).filter(([, v]) => v),
                );
            }
            continue;
        }
        const value = String(draft[key]).trim();
        if (value === String(base[key])) continue;
        const clearable = key === 'milestone_id' || key === 'brief';
        changes[key] = clearable && value === '' ? null : value;
    }
    return changes;
}

/** The grid columns a filter on which a new spec takes, and the field it sets. */
const FILTER_FIELDS = {
    asset_type: 'asset_type',
    priority: 'priority',
    milestone: 'milestone_id',
};

/**
 * The fields a new spec starts with: each column filtered to exactly one value
 * (not "none"), so the spec shows in the current view.
 * @param {import('./smart-grid.js').GridState} state
 * @returns {Record<string, string>}
 */
export function newSpecDefaults(state) {
    /** @type {Record<string, string>} */
    const out = {};
    for (const [column, field] of Object.entries(FILTER_FIELDS)) {
        const values = state.filters[column];
        if (values?.length !== 1 || !values[0]) continue;
        out[field] = values[0];
    }
    return out;
}

/**
 * The positions moved specs take, in order, in the manual order of every spec:
 * evenly spaced just after prev (before whatever follows it), just before
 * next, or at the end with neither. The server works them out the same way
 * (assetops spreadPositions); these show the move before it answers.
 * @param {Spec[]} items - every spec
 * @param {Spec|null} prev
 * @param {Spec|null} next
 * @param {string[]} movingIds
 * @returns {number[]}
 */
export function positionsBetween(items, prev, next, movingIds) {
    const n = movingIds.length;
    const others = items
        .filter((i) => !movingIds.includes(i.id))
        .map((i) => i.position)
        .sort((a, b) => a - b);
    const step = (from) => movingIds.map((_, i) => from + i + 1);
    let lo;
    let hi;
    if (prev) {
        lo = prev.position;
        hi = others.find((p) => p > lo);
        if (hi === undefined) return step(lo);
    } else if (next) {
        hi = next.position;
        const i = others.findIndex((p) => p >= hi);
        if (i <= 0) return step(hi - n - 1);
        lo = others[i - 1];
    } else {
        return step(others.length ? others[others.length - 1] : 0);
    }
    return movingIds.map((_, i) => lo + ((hi - lo) * (i + 1)) / (n + 1));
}
